import { legacyAuthHeader, legacyHostName } from '../utils/legacyHostCompat';
/**
 * JSA (Job Safety Analysis) Service
 * Frontend service for creating, viewing, signing, and exporting JSAs
 */

import { bidBoardAuthHeaders } from './bidBoardAuth';
import { hostWebApp } from './hostWebApp';
const LEGACY_AUTH_USER_ID_HEADER = legacyAuthHeader('User-Id');
const LEGACY_AUTH_USERNAME_HEADER = legacyAuthHeader('Username');
const LEGACY_AUTH_INIT_DATA_HEADER = legacyAuthHeader('Init-Data');


// ─── Types ─────────────────────────────────────────────────────────────────────

export interface JSAHazard {
  hazard: string;
  riskLevel: 'low' | 'medium' | 'high' | 'critical';
  control: string;
}

export interface JSAStep {
  description: string;
  hazards: JSAHazard[];
}

export interface EmergencyContact {
  name: string;
  phone: string;
  role: string;
}

export interface JSASignature {
  id: string;
  userId: string;
  username: string | null;
  role: string;
  name: string;
  signedAt: string;
}

export interface JSASitePhoto {
  id: string;
  jsaId: string;
  userId: string;
  username: string | null;
  originalFilename: string | null;
  mimeType: string;
  fileSize: number;
  caption: string | null;
  photoLatitude: number | null;
  photoLongitude: number | null;
  createdAt: string;
  url: string;
}

export interface JSA {
  jobMasterId?: string | null;
  id: string;
  jsaNumber: number | null;
  userId: string;
  username: string | null;
  crewId: string | null;
  wellId: string | null;
  wellName: string | null;
  apiNumber: string | null;
  operator: string | null;
  facilityType: string | null;
  latitude: number | null;
  longitude: number | null;
  county: string | null;
  state: string | null;
  jobType: string;
  jobDescription: string | null;
  steps: JSAStep[];
  ppeRequired: string[];
  windDirection: string | null;
  windDirectionMode: 'manual' | 'automatic';
  weatherSnapshot: string | null;
  weatherUpdatedAt: number | null;
  musterPoint: string | null;
  musterPointLat: number | null;
  musterPointLng: number | null;
  musterHighlightPath: string | null;
  // Area B (backup muster) — required alongside Area A as of LAYOUT v80+.
  musterPointB: string | null;
  musterPointBLat: number | null;
  musterPointBLng: number | null;
  musterHighlightPathB: string | null;
  nearestHospital: string | null;
  hospitalDirections: string | null;
  emergencyContacts: EmergencyContact[];
  h2sPresent: boolean;
  h2sReading: number | null;
  status: 'draft' | 'active' | 'completed' | 'archived';
  createdAt: string;
  updatedAt: string;
  signatures?: JSASignature[];
  sitePhotos?: JSASitePhoto[];
  departureRadiusM: number;
  signInviteRadiusM: number;
}

export type JSACreateData = Omit<JSA, 'id' | 'userId' | 'username' | 'createdAt' | 'updatedAt' | 'signatures'>;

export interface JSAPromptFacility {
  wellId: string;
  sourceId?: string | null;
  wellName: string;
  apiNumber?: string | null;
  operator?: string | null;
  facilityType?: string | null;
  latitude: number;
  longitude: number;
  county?: string | null;
  state?: string | null;
  jobDescription?: string | null;
  promptId: string;
}

export interface JSAGeofencePrompt {
  prompt: {
    id: string;
    status: string;
    distanceM: number;
  };
  facility: JSAPromptFacility;
}

// ─── Presets ───────────────────────────────────────────────────────────────────

export { COMMON_HAZARDS } from './jsaHazardControls';

export const COMMON_PPE = [
  'Hard Hat',
  'FRC',
  'Steel Toes',
  'Safety Glasses',
  'Gloves',
  'H2S Monitor',
  'Hearing Protection',
  'Fall Protection',
  'Face Shield',
  'Respirator',
  'High-Vis Vest',
  'Chemical Suit',
];

export const ALWAYS_REQUIRED_PPE = [
  'Hard Hat',
  'FRC',
  'Steel Toes',
  'Safety Glasses',
  'Gloves',
  'H2S Monitor',
  'Hearing Protection',
] as const;

export function withAlwaysRequiredPpe(items?: string[] | null): string[] {
  const extra = (items || []).filter(
    (item) => !(ALWAYS_REQUIRED_PPE as readonly string[]).includes(item),
  );
  return [...ALWAYS_REQUIRED_PPE, ...extra];
}

export const JOB_TYPES = [
  'Acidizing',
  'Automation',
  'Bradenhead Test',
  'Casing / Tubing',
  'Cement',
  'Chemical Treatment',
  'Coiled Tubing',
  'Communications',
  'Completions',
  'Compressor Maintenance',
  'Construction',
  'Crane / Rigging',
  'Delivery / Transport',
  'Directional Drilling',
  'Drilling',
  'ESP / Artificial Lift',
  'Electrician',
  'Environmental / Remediation',
  'Equipment Installation',
  'Excavation / Trenching',
  'Fishing',
  'Flowback',
  'Frac',
  'Gas Lift',
  'Gauging / Measurement',
  'Gravel Pack',
  'Hot Oil',
  'Hot Shot',
  'Hydrostatic Test',
  'Hydrovac / Daylighting',
  'Inspection',
  'Leak Detection / Emissions Survey',
  'Logging',
  'Mud / Drilling Fluids',
  'Perforating',
  'Pipeline Maintenance',
  'Pipeline Pigging',
  'Plug & Abandon',
  'Plunger Lift',
  'Pressure Test',
  'Pumping',
  'Rig Down',
  'Rig Move',
  'Rig Up',
  'Rod Pump Service',
  'Roustabout',
  'SCADA / Telemetry',
  'Sandblasting / Coating',
  'Sewer / Wastewater Service',
  'Slickline',
  'Snubbing',
  'Stimulation',
  'Swab',
  'Tank Battery Service',
  'Tank Cleaning / Confined Space',
  'Trucking / Hauling',
  'Utility Locate',
  'VRU (Vapor Recovery Unit)',
  'Vacuum Truck Checklist',
  'Valve Maintenance',
  'Waste Management / Dumpster',
  'Water Transfer',
  'Welding',
  'Well Testing',
  'Wellhead / Christmas Tree',
  'Wireline',
  'Workover',
  'General',
];

export const COMMON_OPERATORS = [
  'Apache Corporation',
  'BTA Oil Producers',
  'Burlington Resources',
  'Chevron',
  'Cimarex Energy',
  'Concho Resources',
  'ConocoPhillips',
  'Continental Resources',
  'Coterra Energy',
  'DCP Midstream',
  'Devon Energy',
  'Diamondback Energy',
  'EOG Resources',
  'Fasken Oil & Ranch',
  'Halliburton',
  'Jagged Peak Energy',
  'Mewbourne Oil Company',
  'Occidental Petroleum',
  'Oxy (Permian)',
  'Penn Virginia',
  'Pioneer Natural Resources',
  'Plains Exploration',
  'Range Resources',
  'Schlumberger',
  'SM Energy',
  'Targa Resources',
  'XTO Energy',
  'Yates Petroleum',
];

export const COMMON_CONTROLS = [
  'Barricade area',
  'Buddy system',
  'Continuous gas monitoring',
  'De-energize / LOTO',
  'Engineering controls',
  'Escape pack / SCBA',
  'Fall arrest system',
  'Fire extinguisher on site',
  'Flag / barricade area',
  'Follow safe work procedure',
  'Ground fault protection',
  'Hot work permit',
  'Inspect equipment before use',
  'JSA review with all personnel',
  'Keep clear of suspended loads',
  'Maintain 3-point contact',
  'Monitor wind direction',
  'Positive communication',
  'Pre-job safety meeting',
  'Proper lifting techniques',
  'Reduce speed / use spotter',
  'Secure all loads',
  'Use designated walkways',
  'Use rated rigging/lifting equipment',
  'Verify zero energy state',
  'Wear required PPE',
  'Wind sock visible',
];

export const NM_COUNTIES = [
  'Bernalillo', 'Catron', 'Chaves', 'Cibola', 'Colfax', 'Curry', 'De Baca',
  'Dona Ana', 'Eddy', 'Grant', 'Guadalupe', 'Harding', 'Hidalgo', 'Lea',
  'Lincoln', 'Los Alamos', 'Luna', 'McKinley', 'Mora', 'Otero', 'Quay',
  'Rio Arriba', 'Roosevelt', 'San Juan', 'San Miguel', 'Sandoval', 'Santa Fe',
  'Sierra', 'Socorro', 'Taos', 'Torrance', 'Union', 'Valencia',
];

export const TX_COUNTIES = [
  'Andrews', 'Borden', 'Cottle', 'Crane', 'Crockett', 'Dawson', 'Ector',
  'Fisher', 'Gaines', 'Garza', 'Glasscock', 'Hockley', 'Howard', 'Kent',
  'King', 'Lamb', 'Loving', 'Lubbock', 'Martin', 'Midland', 'Mitchell',
  'Motley', 'Nolan', 'Pecos', 'Reagan', 'Reeves', 'Scurry', 'Sterling',
  'Terry', 'Terrell', 'Tom Green', 'Upton', 'Ward', 'Winkler', 'Yoakum',
];

export const SIGNATURE_ROLES = [
  'Worker',
  'Supervisor',
  'Safety Man',
  'Crew Lead',
  'Company Man',
  'Operator Rep',
];

export const WIND_DIRECTIONS = [
  'N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW', 'Calm', 'Variable',
];

// ─── Auth helpers ──────────────────────────────────────────────────────────────

function getHeaders(): HeadersInit {
  let userId = hostWebApp.getUserId() || '';
  let username = (window as any)[legacyHostName()]?.WebApp?.initDataUnsafe?.user?.username || '';

  if (!userId) {
    const storedId = localStorage.getItem('dev-user-id');
    const storedName = localStorage.getItem('dev-username');
    if (storedId) {
      userId = storedId;
    } else {
      userId = typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? `dev-${crypto.randomUUID()}`
        : `dev-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem('dev-user-id', userId);
    }
    if (storedName) {
      username = storedName;
    } else {
      username = 'web-user';
      localStorage.setItem('dev-username', username);
    }
  }

  return {
    ...bidBoardAuthHeaders(),
    'Content-Type': 'application/json',
    [LEGACY_AUTH_USER_ID_HEADER]: userId,
    [LEGACY_AUTH_USERNAME_HEADER]: username,
    [LEGACY_AUTH_INIT_DATA_HEADER]: hostWebApp.getInitData(),
  };
}

function getAuthHeaders(): HeadersInit {
  const headers = getHeaders() as Record<string, string>;
  const { 'Content-Type': _contentType, ...authHeaders } = headers;
  return authHeaders;
}

const API_BASE = '/api/jsa';

async function throwJsaApiError(res: Response, fallback: string): Promise<never> {
  const payload = await res.json().catch(() => ({} as { error?: string; field?: string; message?: string }));
  const parts = [payload.error || fallback];
  if (payload.field) parts.push(String(payload.field));
  if (payload.message && payload.message !== payload.error) parts.push(String(payload.message));
  throw new Error(parts.join(' — '));
}

// ─── Service ───────────────────────────────────────────────────────────────────

export const jsaService = {
  async create(data: Partial<JSACreateData> & { promptId?: string | null }): Promise<{ success: boolean; id: string }> {
    const res = await fetch(API_BASE, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) await throwJsaApiError(res, 'Failed to create JSA');
    return res.json();
  },

  async get(id: string): Promise<JSA> {
    const res = await fetch(`${API_BASE}/${id}`, { headers: getHeaders() });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to get JSA');
    return res.json();
  },

  async list(params?: { jobMasterId?: string; wellId?: string; apiNumber?: string; wellName?: string; latitude?: number | null; longitude?: number | null; status?: string; limit?: number; offset?: number; signal?: AbortSignal }): Promise<{ jsas: JSA[]; count: number; nextOffset?: number | null }> {
    const sp = new URLSearchParams();
    if (params?.jobMasterId) sp.set('jobMasterId', params.jobMasterId);
    if (params?.wellId) sp.set('wellId', params.wellId);
    if (params?.apiNumber) sp.set('apiNumber', params.apiNumber);
    if (params?.wellName) sp.set('wellName', params.wellName);
    if (params?.latitude != null) sp.set('latitude', String(params.latitude));
    if (params?.longitude != null) sp.set('longitude', String(params.longitude));
    if (params?.offset) sp.set('offset', String(params.offset));
    if (params?.status) sp.set('status', params.status);
    if (params?.limit) sp.set('limit', String(params.limit));
    const res = await fetch(`${API_BASE}?${sp.toString()}`, { headers: getHeaders(), signal: params?.signal });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to list JSAs');
    return res.json();
  },

  async update(id: string, data: Partial<JSACreateData>): Promise<{ success: boolean }> {
    const res = await fetch(`${API_BASE}/${id}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) await throwJsaApiError(res, 'Failed to update JSA');
    return res.json();
  },

  async listPhotos(jsaId: string): Promise<JSASitePhoto[]> {
    const res = await fetch(`${API_BASE}/${jsaId}/photos`, { headers: getHeaders() });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to list JSA site photos');
    const data = await res.json();
    return data.photos || [];
  },

  async uploadPhoto(
    jsaId: string,
    file: File,
    data?: { caption?: string; latitude?: number | null; longitude?: number | null },
  ): Promise<JSASitePhoto> {
    const formData = new FormData();
    formData.append('photo', file);
    if (data?.caption) formData.append('caption', data.caption);
    if (data?.latitude != null) formData.append('latitude', String(data.latitude));
    if (data?.longitude != null) formData.append('longitude', String(data.longitude));

    const res = await fetch(`${API_BASE}/${jsaId}/photos`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
    });
    const result = await res.json();
    if (!res.ok) throw new Error(result.error || 'Failed to upload JSA site photo');
    return result.photo;
  },

  async deletePhoto(jsaId: string, photoId: string): Promise<{ success: boolean }> {
    const res = await fetch(`${API_BASE}/${jsaId}/photos/${photoId}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to delete JSA site photo');
    return res.json();
  },

  async sign(jsaId: string, name: string, role: string, signatureData?: string): Promise<{ success: boolean; signatureId: string }> {
    const res = await fetch(`${API_BASE}/${jsaId}/sign`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ name, role, signatureData }),
    });
    if (!res.ok) await throwJsaApiError(res, 'Failed to sign JSA');
    return res.json();
  },

  async complete(jsaId: string): Promise<{ success: boolean }> {
    const res = await fetch(`${API_BASE}/${jsaId}/complete`, {
      method: 'POST',
      headers: getHeaders(),
    });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to complete JSA');
    return res.json();
  },

  async getTemplate(wellId: string): Promise<{ template: JSA | null }> {
    const res = await fetch(`${API_BASE}/templates?wellId=${wellId}`, { headers: getHeaders() });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to get template');
    return res.json();
  },

  async nearby(lat: number, lng: number, radiusMiles = 5): Promise<{ jsas: JSA[]; count: number }> {
    const res = await fetch(`${API_BASE}/nearby?lat=${lat}&lng=${lng}&radius=${radiusMiles}`, { headers: getHeaders() });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to get nearby JSAs');
    return res.json();
  },

  async getPrompt(promptId: string): Promise<JSAGeofencePrompt> {
    const res = await fetch(`${API_BASE}/prompts/${encodeURIComponent(promptId)}`, { headers: getHeaders() });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to load JSA prompt');
    return res.json();
  },

  getPdfUrl(jsaId: string): string {
    return `${API_BASE}/${jsaId}/pdf`;
  },
};
